import React, { useState, useEffect } from 'react';
import { TrendingUp, MessageCircle, Award, X } from 'lucide-react';

/**
 * SocialProofNudge — Shows engagement nudge toasts after login.
 * Contextual messages that drive action without being annoying.
 */
const NUDGE_POOL = [
  { icon: TrendingUp, color: 'text-emerald-600 bg-emerald-50', message: (n) => `${n} users practised today. Join them!` },
  { icon: MessageCircle, color: 'text-blue-600 bg-blue-50', message: () => `You're 2 conversations away from your next streak milestone` },
  { icon: Award, color: 'text-purple-600 bg-purple-50', message: () => `3 people earned badges this hour. You could be next!` },
  { icon: TrendingUp, color: 'text-orange-600 bg-orange-50', message: () => `Your confidence score is in the top 25% this week` },
  { icon: MessageCircle, color: 'text-indigo-600 bg-indigo-50', message: () => `Pro tip: try a 5-minute practice session before lunch` },
];

const SocialProofNudge = () => {
  const [nudge, setNudge] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show once per session
    if (sessionStorage.getItem('hibff_nudge_shown')) return;

    // Pick a random nudge
    const randomNudge = NUDGE_POOL[Math.floor(Math.random() * NUDGE_POOL.length)];
    const todayCount = Math.floor(Math.random() * 40) + 12; // 12-52

    // Delay appearance by 3 seconds after mount
    const timer = setTimeout(() => {
      setNudge({ ...randomNudge, text: randomNudge.message(todayCount) });
      setVisible(true);
      sessionStorage.setItem('hibff_nudge_shown', 'true');
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => setVisible(false), 8000);
    return () => clearTimeout(timer);
  }, [visible]);

  if (!visible || !nudge) return null;

  const Icon = nudge.icon;
  const [textColor, bgColor] = nudge.color.split(' ');

  return (
    <div
      className="fixed bottom-24 left-4 right-4 sm:left-auto sm:right-6 z-40 animate-slideUp"
      data-testid="social-proof-nudge"
    >
      <div className="relative flex items-start gap-3 max-w-sm mx-auto sm:mx-0 bg-white rounded-xl shadow-lg border border-gray-200 p-4 pr-10">
        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${bgColor}`}>
          <Icon size={18} className={textColor} />
        </div>
        <p className="text-sm text-gray-700 font-medium leading-snug">{nudge.text}</p>
        <button
          onClick={() => setVisible(false)}
          className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 min-w-[40px] min-h-[40px] flex items-center justify-center"
          data-testid="nudge-dismiss"
          aria-label="Dismiss notification"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default SocialProofNudge;
